import { isUndefined } from "lodash";
import { store } from "redux/store";

export const formatDate = (date: Date): string => {
  const pad = (num: number, size: number = 2): string => String(num).padStart(size, "0");
  return (
    date.getFullYear() +
    "-" +
    pad(date.getMonth() + 1) +
    "-" +
    pad(date.getDate()) +
    "T" +
    pad(date.getHours()) +
    ":" +
    pad(date.getMinutes()) +
    ":" +
    pad(date.getSeconds()) +
    "." +
    pad(date.getMilliseconds(), 3)
  );
};

export const getLoggedInUserDetails = (skipImpersonate: any = false) => {
  const state: any = store.getState();
  const { isImpersonated, userDetails } = state.app.impersonationDetails;
  return isImpersonated && !skipImpersonate ? userDetails : state.app.userName;
};

export const getUserGroup = (userType: string, isConsultant: boolean) => {
  switch (userType) {
    case "CC":
    case "CE":
      return isConsultant ? "Consultant" : "Corporate";

    case "OW":
      return "Above Property";

    default:
      return "Hotel User";
  }
};

const getIsLeadership = (userType: string, viewAs: string, leadershipType: any) => {
  if (userType === "OW" && viewAs === "My View") {
    return true;
  }
  return leadershipType;
};

export const removeParamFromSearch = (search: any, param = "code") => {
  if (!search) return "";
  const sp = new URLSearchParams(search);
  sp.delete(param);
  const s = sp.toString(); // returns "" if no params left
  return s ? `?${s}` : "";
};

/**
 * Decide whether the current path should be ignored for a given user.
 * - Returns `false` for pages that should NOT be ignored,
 *   and `true` otherwise.
 */
export const pathToBeIgnorePerUser = (userDetails: any): boolean => {
  const path = window.location.pathname;
  const params = new URLSearchParams(window.location.search);

  // Build a copy of params without the "code" param and check if any remain.
  const paramsWithoutCode = new URLSearchParams(params.toString());
  paramsWithoutCode.delete("code");
  const hasQueryAfterRemovingCode = Array.from(paramsWithoutCode.keys()).length > 0;

  const parentTab = params.get("parentTab");
  const hasChildTab = params.has("childTab");
  const hasTab = params.has("tab");

  const isRootNoQuery = path === "/" && !hasQueryAfterRemovingCode;
  const isLibraryNoQuery = path === "/library" || path === "/library/search";
  const lobbyUserType = userDetails?.lobbyUserType;
  const roleCategory = userDetails?.roleCategory;
  if (lobbyUserType === "OW") {
    // For OW: allowed only when root and no query (after removing code)
    if (isRootNoQuery) return false;
    if (isLibraryNoQuery) {
      return hasTab;
    }
  }

  if (lobbyUserType === "HF") {
    if (roleCategory === "Leadership") {
      // For HF Leadership: allowed when root & no query OR parentTab === "Property Overview" without childTab
      if (isRootNoQuery) return false;
      if (parentTab === "Property Overview" && !hasChildTab) return false;
      return true;
    }
    return true;
  }
  if (lobbyUserType === "CC") {
    return !isRootNoQuery;
  }
  return true;
};

const formatRouteBreadcrumb = (url: string, userType: string): string => {
  if (url) {
    // If relative path, prepend dummy base
    const parsedUrl = url.startsWith("http") ? new URL(url) : new URL(url, "http://dummy-base");

    // Pathname (e.g. /library → "library", / → "")
    let path = parsedUrl.pathname.replace(/^\/+/, ""); // remove leading slash

    // Collect query params
    const params = new URLSearchParams(parsedUrl.search);

    const parentTab = params.get("parentTab") || params.get("tab");
    const childTab = params.get("childTab");

    // Proper decode only
    const clean = (str: string | null): string => (str ? decodeURIComponent(str) : "");

    if (path === "") {
      const defaultPaths: Record<string, string> = {
        OW: "My Dashboard",
        CC: "My Lists"
      };
      path = defaultPaths[userType] || "Home";
    }

    // Build breadcrumb parts
    return [path, clean(parentTab), clean(childTab)].filter(Boolean).join(" / ");
  }
  return "";
};

export const commonApiCallParam = (isPopup?: any, myPlanDetails?: any) => {
  const currentUrl = window.location.pathname;

  const current = window.location.pathname + removeParamFromSearch(window.location.search, "code");
  const now = new Date();
  const userDetails = getLoggedInUserDetails();
  const state = store.getState() as any; // RootState
  const loggedInUserDetails = state.app.loggedInUserDetails;
  const userName = state.app.userName;
  const impersonationDetails = state.app.impersonationDetails;
  const previousRoute = state.app.previousRoute;
  const libraryPlan = state.planDetails.planDetails;
  const librarySearch = state.Library.sharedLinkDetails;
  const viewAs = state.app.viewAsForAbovePropUser;
  const innCode = state.app.innCode;
  const hasLibrarySearch = window.location.pathname.includes("library/search");
  const queryParams = new URLSearchParams(window.location.search);
  const hasUUID = queryParams.has("uuid");
  const param: any = {
    visitedPage: formatRouteBreadcrumb(current, userDetails?.lobbyUserType),
    visitorHltId: userName?.hltId,
    userType: getUserGroup(userDetails?.lobbyUserType, loggedInUserDetails?.isConsultant),
    isLeadershipUser: getIsLeadership(userDetails?.lobbyUserType, viewAs, loggedInUserDetails?.isLeadership),
    isImpersonation: impersonationDetails?.isImpersonated,
    visitedDate: formatDate(now),
    fromPage: formatRouteBreadcrumb(previousRoute, userDetails?.lobbyUserType),
    popup: isPopup,
    consumerId: localStorage.getItem("X-ConsumerID"),
    impersonateTo: impersonationDetails?.userDetails?.hltId,
    buzzVisit: false
  };
  if (
    (currentUrl.startsWith("/myplandetails/") || currentUrl.startsWith("/inncodeplandetails/")) &&
    !isUndefined(myPlanDetails)
  ) {
    param.planName = myPlanDetails?.name;
    param.planVisit = true;
    param.planId = myPlanDetails.id;
    param.cmsPlanId = myPlanDetails.cmsPlanId;
  }
  if (currentUrl.startsWith("/plandetails/")) {
    param.planName = libraryPlan?.name;
    param.planVisit = true;
    param.cmsPlanId = libraryPlan?.id;
    param.innCode = innCode;
  }
  if (hasLibrarySearch && hasUUID) {
    param.innCode = librarySearch.hltId;
    param.searchText = librarySearch.searchText;
    param.sharedLinkId = librarySearch.id;
    param.sharedLinkType = librarySearch.type;
    param.innCode = innCode;
  }

  return param;
};

export const resourceLinks = (param: any, activeCard: any, resources: any) => {
  if (isLibraryPage() || isFunctionalArea()) {
    param.cmsCardId = activeCard.cardId;
  } else {
    param.cardId = activeCard.cardId;
    param.cmsCardId = activeCard.cmsCardId;
  }
  param.cardVisit = true;
  param.cardTitle = activeCard.name;
  if (resources) {
    param.resourceLinkId = resources.id;
    param.resourceLinkUrl = resources.url;
  }
  return param;
};

export const isLibraryPage = () => {
  const currentUrl = window.location.pathname;
  return (
    currentUrl.startsWith("/plandetails/") &&
    (!currentUrl.startsWith("/myplandetails/") || !currentUrl.startsWith("/inncodeplandetails/"))
  );
};

export const isFunctionalArea = () => {
  const currentUrl = window.location.href;
  return currentUrl.includes("functionalArea");
};

export const updatePopupHeader = (title: string) => {
  return title;
};
